import * as React from 'react';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Backdrop from '@mui/material/Backdrop';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import { alpha } from '@mui/material/styles';

import apiClient, { shouldRedirectToLogin, getErrorMessage } from '../services/api-client';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import { Navigate, useNavigate } from 'react-router-dom';

import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';

import TextField from '@mui/material/TextField';

import AppConfig from '../services/app-config';
import AuthService from '../services/auth.js';
import notificationService from '../services/notification-service.js';
import IconButton from '@mui/material/IconButton';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { ColorModeContext } from '../ColorModeContext';

export default function Join({setNavbarState, navbarState}) {
  const [redirect, setRedirect] = React.useState({ trigger: false, route: '/' })
  const [loadingPreview, setLoadingPreview] = React.useState(true)
  const [snackbarOpen, setSnackbarOpen] = React.useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = React.useState('warning');
  const [notificationsEnabled, setNotificationsEnabled] = React.useState(AppConfig.getLocal('notification_enable'));
  const [snackbarMessage, setSnackbarMessage] = React.useState("");
  const [userData, setUserData] = React.useState([]);
  const navigate = useNavigate();
  const colorMode = React.useContext(ColorModeContext);
  const [savingProfile, setSavingProfile] = React.useState(false);
  const [profileLoaded, setProfileLoaded] = React.useState(false);
  const [profileDirty, setProfileDirty] = React.useState(false);
  const autosaveTimeoutRef = React.useRef(null);
  const formDataRef = React.useRef(null);
  const [formData, setFormData] = React.useState({
    name: '',
    lastname: '',
    phone: '',
    appear_in_directory: false,
  });

  React.useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  React.useEffect(() => {
    setNavbarState({...navbarState, showBack: true, showOptions: false, showSearch: false})
    apiClient.get('users/me', {}, { action: 'fetch_user_profile' })
      .then((response) => {
        if (response.status === 200) {
          setUserData(response.data.data);
          setFormData({
            name: response.data.data?.name || '',
            lastname: response.data.data?.lastname || '',
            phone: response.data.data?.phone || '',
            appear_in_directory: Boolean(response.data.data?.appear_in_directory),
          });
          setProfileLoaded(true);
          setProfileDirty(false);
          setLoadingPreview(false);
        } else if (response.status === 401) {
          setRedirect({ trigger: true, route: '/login' });
        }
      })
      .catch((error) => {
        setRedirect({ trigger: true, route: '/login' });
      })
  }, []);

  const saveProfile = React.useCallback(async () => {
    if (!profileLoaded) {
      return;
    }
    if (!profileDirty) {
      return;
    }
    if (savingProfile) {
      return;
    }

    const payload = { ...(formData || {}) };
    setSavingProfile(true);
    try {
      const response = await apiClient.post('users/update/me', payload, {}, { action: 'update_user_profile' });
      AppConfig.parseResponse(response);
      if (response.status === 200) {
        setUserData((prev) => ({ ...(prev || {}), ...(payload || {}) }));
        setProfileDirty((prevDirty) => {
          if (!prevDirty) {
            return false;
          }
          const current = formDataRef.current;
          const stillSame = current ? JSON.stringify(payload) === JSON.stringify(current) : true;
          return stillSame ? false : true;
        });
        setSnackbarSeverity('success');
        setSnackbarMessage('Cambios guardados');
        setSnackbarOpen(true);
      } else {
        setSnackbarSeverity('error');
        setSnackbarMessage('No se pudieron guardar los cambios');
        setSnackbarOpen(true);
      }
    } catch (error) {
      if (shouldRedirectToLogin(error)) {
        setRedirect({ trigger: true, route: '/login' });
        return;
      }
      setSnackbarSeverity('error');
      setSnackbarMessage(getErrorMessage(error) || 'Error al guardar cambios');
      setSnackbarOpen(true);
    } finally {
      setSavingProfile(false);
    }
  }, [formData, profileDirty, profileLoaded, savingProfile]);

  React.useEffect(() => {
    if (!profileLoaded || !profileDirty) {
      return undefined;
    }

    if (autosaveTimeoutRef.current) {
      clearTimeout(autosaveTimeoutRef.current);
    }

    autosaveTimeoutRef.current = setTimeout(() => {
      saveProfile();
    }, 750);

    return () => {
      if (autosaveTimeoutRef.current) {
        clearTimeout(autosaveTimeoutRef.current);
      }
    };
  }, [formData, profileDirty, profileLoaded, saveProfile]);
  
  const handleClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  const [loading, setLoading] = React.useState(false);

  const handleLogout = React.useCallback(async () => {
    setLoading(true);
    try {
      await notificationService.unregisterToken();
    } catch (error) {
      // ignore, continue logout
    } finally {
      AuthService.logout();
      setRedirect({ trigger: true, route: '/login' });
      setLoading(false);
    }
  }, []);

  const requestNotificationPermission = React.useCallback(async () => {
    setLoading(true);
    try {
      const success = await notificationService.requestPermissionAndRegister();
      if (success) {
        setNotificationsEnabled(true);
        setSnackbarSeverity('success');
        setSnackbarMessage('Notificaciones habilitadas correctamente');
        setSnackbarOpen(true);
      } else {
        setSnackbarSeverity('warning');
        setSnackbarMessage('Permiso de notificaciones denegado');
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error('Error enabling notifications:', error);
      setSnackbarSeverity('error');
      setSnackbarMessage('Error al habilitar notificaciones');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleNotificationToggle = (event) => {
    if (event.target.checked) {
      requestNotificationPermission();
    } else {
      unregisterNotifications();
    }
  }

  const unregisterNotifications = async () => {
    setLoading(true);
    try {
      await notificationService.unregisterToken();
      setNotificationsEnabled(false);
      setSnackbarSeverity('success');
      setSnackbarMessage('Notificaciones deshabilitadas');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error disabling notifications:', error);
      setNotificationsEnabled(false);
      setSnackbarSeverity('error');
      setSnackbarMessage('Error al deshabilitar notificaciones');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  }


  const renderUnit = () => {
    if (userData.role === "receptor" || userData.role === "admin") {
      return "Unidad"
    } else {
      return AppConfig.get('unit_type')
    }
  }

  return (
    <Container maxWidth="sm" disableGutters>
      {redirect.trigger && (
        <Navigate to={redirect.route} replace={true} />
      )}
      <Snackbar open={snackbarOpen} autoHideDuration={3000} onClose={handleClose} anchorOrigin={{ vertical: "top", horizontal: "center" }} sx={{mt: "3.2rem"}}>
        <Alert onClose={handleClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', pb: 6 }}>
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 10,
            px: 3,
            pt: 2,
            pb: 1.5,
            backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.95),
            backdropFilter: 'blur(10px)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton
              onClick={() => navigate(-1)}
              sx={{
                width: 40,
                height: 40,
                ml: -1,
                color: 'text.primary',
                '&:hover': { backgroundColor: (theme) => alpha(theme.palette.text.primary, 0.08) },
              }}
            >
              <ArrowBackIcon sx={{ fontSize: 28 }} />
            </IconButton>
            <Typography sx={{ fontSize: 24, fontWeight: 800, color: 'text.primary', letterSpacing: '-0.02em' }}>
              Mi Perfil
            </Typography>
          </Box>
        </Box>

        <Backdrop
          sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={loadingPreview || loading}
        >
          <CircularProgress color="inherit" />
        </Backdrop>

        {!loadingPreview &&
          (
            <Box sx={{ px: 3, mt: 1.5, display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column' }} data-tour="profile-fields">
                <TextField
                  size="medium"
                  type="text"
                  id="name"
                  value={formData.name}
                  sx={{ mb: 2 }}
                  label="Nombre"
                  variant="outlined"
                  onChange={(e) => {
                    setFormData({ ...formData, name: e.target.value });
                    setProfileDirty(true);
                  }}
                />
                <TextField
                  size="medium"
                  type="text"
                  id="lastname"
                  value={formData.lastname}
                  sx={{ mb: 2 }}
                  label="Apellido"
                  variant="outlined"
                  onChange={(e) => {
                    setFormData({ ...formData, lastname: e.target.value });
                    setProfileDirty(true);
                  }}
                />
                <TextField
                  size="medium"
                  type="tel"
                  id="phone"
                  value={formData.phone}
                  sx={{ mb: 2 }}
                  label="Numero Telefonico"
                  variant="outlined"
                  onChange={(e) => {
                    setFormData({ ...formData, phone: e.target.value });
                    setProfileDirty(true);
                  }}
                />

                {userData.role !== "receptor" && AppConfig.get("modules").includes("directory") ? (
                  <FormControlLabel
                    control={(
                      <Switch
                        checked={Boolean(formData.appear_in_directory)}
                        onChange={(e) => {
                          setFormData({ ...formData, appear_in_directory: e.target.checked });
                          setProfileDirty(true);
                        }}
                        name="appearInDirectory"
                      />
                    )}
                    label="Aparecer en Directorio"
                    sx={{ mb: 1.5, ml: 1 }}
                  />
                ) : ''}

                <FormControlLabel
                  control={
                    <Switch
                      checked={notificationsEnabled}
                      onChange={handleNotificationToggle}
                      name="receiveNotifications"
                    />
                  }
                  label="Recibir Notificaciones"
                  data-tour="profile-notifications"
                  sx={{ mb: 1.5, ml: 1 }}
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={colorMode.mode === 'dark'}
                      onChange={colorMode.toggleColorMode}
                      name="darkMode"
                    />
                  }
                  label="Modo oscuro"
                  data-tour="profile-darkmode"
                  sx={{ mb: 1.5, ml: 1 }}
                />

                {AuthService.userRoleIncludes([]) && notificationsEnabled && (
                  <Typography variant="caption" sx={{ mt: -2, ml: 3, mb: 1 }} color="text.secondary">
                    {AppConfig.getLocal('notification_token_id')}
                  </Typography>
                )}

                <Divider sx={{ my: 2.5 }} />

                <TextField
                  disabled
                  size="medium"
                  type="email"
                  id="mail"
                  value={userData.username || ''}
                  sx={{ mb: 1.2 }}
                  label="Correo Electronico"
                  variant="outlined"
                  helperText="*Contacte al administrador para cambiar su correo."
                />
                <TextField
                  disabled
                  value={userData.unit || ''}
                  size="medium"
                  type="text"
                  id="apartment"
                  sx={{ mb: 1.2 }}
                  label={renderUnit()}
                  variant="outlined"
                />
              </Box>

              <Box sx={{ mt: 3 }}>
                <Button
                  fullWidth
                  size="large"
                  variant="outlined"
                  onClick={handleLogout}
                  sx={{
                    mt: 1.5,
                    py: 1.2,
                    borderRadius: '10px',
                    textTransform: 'none',
                    fontWeight: 800,
                    color: 'text.primary',
                    borderColor: 'divider',
                    '&:hover': {
                      borderColor: 'divider',
                    },
                    display: { xs: 'flex', sm: 'none' },
                  }}
                >
                  Cerrar sesión
                </Button>
              </Box>
            </Box>
          )
        }
      </Box>
    </Container>
  );
}
